import * as React from "react"
import { cn } from "../../lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  enableCodeEditor?: boolean;
  language?: "javascript" | "typescript" | "python" | "csharp" | "java";
  showLineNumbers?: boolean;
  errors?: string[];
  highlightLines?: number[];
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ 
    className, 
    enableCodeEditor, 
    language, 
    showLineNumbers = true,
    errors = [],
    highlightLines = [],
    ...props 
  }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const [lineCount, setLineCount] = React.useState(1);
    const [editorHeight, setEditorHeight] = React.useState<number>(0);
    const lineNumbersRef = React.useRef<HTMLDivElement>(null);

    // Merge the forwarded ref with our internal ref
    React.useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    // Special styling for code editor mode
    const codeEditorClass = enableCodeEditor 
      ? "font-mono bg-[#1E1E1E] text-[#D4D4D4] border-[#3E3E42] focus:border-[#007ACC] whitespace-pre"
      : "";
    
    // Add line numbers if enabled
    const lineNumbersClass = enableCodeEditor && showLineNumbers
      ? "pl-10"
      : "";
      
    // Language-specific syntax highlighting class
    const languageClass = enableCodeEditor && language
      ? `language-${language}`
      : "";
      
    // Error highlighting
    const hasErrors = errors.length > 0;
    const errorClass = hasErrors
      ? "border-red-500 focus:border-red-500"
      : "";

    // Handle content changes to update line numbers
    const handleContentChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const content = e.target.value || "";
      const newLineCount = (content.match(/\n/g) || []).length + 1;
      setLineCount(newLineCount);
      
      if (props.onChange) {
        props.onChange(e);
      }
    }, [props.onChange]);

    // Sync textarea and line numbers heights
    React.useEffect(() => {
      if (textareaRef.current && enableCodeEditor && showLineNumbers) {
        const height = textareaRef.current.scrollHeight;
        setEditorHeight(height);
      }
    }, [props.value, enableCodeEditor, showLineNumbers]);

    // Handle tab key for code indentation
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Tab' && enableCodeEditor) {
        e.preventDefault();
        const start = e.currentTarget.selectionStart;
        const end = e.currentTarget.selectionEnd;
        const value = e.currentTarget.value;
        
        // Insert tab at cursor position
        const newValue = value.substring(0, start) + '  ' + value.substring(end);
        
        // Update value and cursor position
        if (textareaRef.current) {
          textareaRef.current.value = newValue;
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + 2;
          
          // Trigger change event to update line numbers
          const event = new Event('input', { bubbles: true });
          textareaRef.current.dispatchEvent(event);
          
          // Call the onChange handler if provided
          if (props.onChange) {
            const syntheticEvent = {
              target: textareaRef.current,
              currentTarget: textareaRef.current
            } as React.ChangeEvent<HTMLTextAreaElement>;
            props.onChange(syntheticEvent);
          }
        }
      }

      // Call the original onKeyDown if provided
      if (props.onKeyDown) {
        props.onKeyDown(e);
      }
    };
      
    return (
      <div className={`relative ${hasErrors ? 'mb-6' : ''}`}>
        <textarea
          className={cn(
            "flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            codeEditorClass,
            lineNumbersClass,
            languageClass,
            errorClass,
            className
          )}
          ref={(el) => {
            textareaRef.current = el;
            if (typeof ref === 'function') ref(el);
            else if (ref) ref.current = el;
          }}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          spellCheck={!enableCodeEditor}
          {...props}
        />
        
        {/* Error messages */}
        {hasErrors && (
          <div className="absolute -bottom-5 left-0 text-xs text-red-500">
            {errors[0]} {/* Show only first error for space */}
            {errors.length > 1 && ` (+${errors.length - 1} more)`}
          </div>
        )}
        
        {/* Line numbers for code editor */}
        {enableCodeEditor && showLineNumbers && (
          <div 
            ref={lineNumbersRef}
            className="absolute top-0 left-0 pt-2 pl-2 h-full w-8 bg-[#252526] border-r border-[#3E3E42] text-[#858585] text-xs flex flex-col select-none overflow-hidden"
            style={{ height: editorHeight > 0 ? `${editorHeight}px` : '100%' }}
          >
            {Array.from({ length: lineCount }).map((_, i) => (
              <div 
                key={i} 
                className={`leading-tight ${
                  highlightLines.includes(i + 1) ? 'text-white font-bold' : ''
                }`}
              >
                {i + 1}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }